import qrcode from 'qrcode-generator';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { endpoints } from '../services/api';
import { useSession } from '../store/session';
import { buttonClass, ghostButtonClass } from './settings/controls';

interface Pairing {
  pairingId: string;
  pollToken: string;
  code: string;
  expiresAt: string;
}

/** TV side of "sign in with your phone": shows a code + QR code and polls until approved. */
export function PairWithPhone({ onCancel }: { onCancel: () => void }) {
  const load = useSession((s) => s.load);
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [status, setStatus] = useState<'starting' | 'pending' | 'expired' | 'error'>('starting');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('starting');
    endpoints
      .pairStart()
      .then((p) => {
        if (cancelled) return;
        setPairing(p);
        setStatus('pending');
      })
      .catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (!pairing || status !== 'pending') return;
    const timer = window.setInterval(async () => {
      try {
        const r = await endpoints.pairPoll(pairing.pairingId, pairing.pollToken);
        if (r.status === 'approved') {
          window.clearInterval(timer);
          await load(); // picks up the new session cookie
        } else if (r.status === 'expired') {
          setStatus('expired');
        }
      } catch {
        // transient – keep polling
      }
    }, 3000);
    const expiry = window.setTimeout(() => setStatus('expired'), new Date(pairing.expiresAt).getTime() - Date.now());
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(expiry);
    };
  }, [pairing, status, load]);

  const url = pairing ? `${window.location.origin}/pair?code=${pairing.code}` : '';
  const qr = useMemo(() => {
    if (!url) return null;
    const q = qrcode(0, 'M');
    q.addData(url);
    q.make();
    return q.createDataURL(6, 2);
  }, [url]);

  if (status === 'error') {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-400">Couldn’t start pairing. Check the connection and try again.</p>
        <button className={ghostButtonClass} onClick={() => setAttempt((a) => a + 1)}>
          Try again
        </button>
      </div>
    );
  }
  if (!pairing || status === 'starting') {
    return (
      <p className="muted flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Getting a code…
      </p>
    );
  }

  const pretty = `${pairing.code.slice(0, 4)}-${pairing.code.slice(4)}`;
  return (
    <div className="flex flex-wrap items-center gap-5">
      {qr && <img src={qr} alt="QR code to sign in on your phone" className="h-36 w-36 rounded-lg bg-white p-1" />}
      <div className="min-w-0 space-y-2">
        {status === 'expired' ? (
          <>
            <p className="text-sm">That code has expired.</p>
            <button className={buttonClass} onClick={() => setAttempt((a) => a + 1)}>
              Get a new code
            </button>
          </>
        ) : (
          <>
            <p className="text-sm">
              Scan the code, or go to <strong>{window.location.host}/pair</strong> and enter:
            </p>
            <p className="font-mono text-3xl font-semibold tracking-[0.15em]" aria-label={`Code ${pairing.code.split('').join(' ')}`}>
              {pretty}
            </p>
            <p className="muted flex items-center gap-2 text-xs">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Waiting for your phone…
            </p>
          </>
        )}
        <button className="muted text-xs hover:underline" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
